import Select from "common/Select/Select";
import { LanguageNameSpaces, SortingOrder } from "@shared/types";
import { Ascending, Descending } from "common/Icons/Icons";
import React, { CSSProperties, useMemo, useState } from "react";
import Flex from "common/Boxes/Flex";
import styled, { useTheme } from "styled-components";
import Button from "common/Buttons/Button";
import Dropdown from "common/Dropdown/Dropdown";
import Text from "common/Typography/Text";
import { useBottomNavigator } from "utils/hooks/misc";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import NewLookSortControl from "common/Select/NewLookSortControl.tsx";

interface SortSelectProps<T> {
  sortBy?: T;
  setSortBy: (sortBy: T) => void;
  sortDirection: SortingOrder;
  setSortDirection: (sortDirection: SortingOrder) => void;
  sortValues: T[];
  selectStyle?: CSSProperties;
  rounded?: boolean;
  isSingleFiltersChipsRow?: boolean;
  placeholder?: string;
  labelMapper?: (value: T) => string;
}

const SortSelect = <T extends string>({
  sortBy,
  setSortBy,
  sortDirection,
  setSortDirection,
  sortValues,
  rounded,
  isSingleFiltersChipsRow,
  placeholder,
  labelMapper,
}: SortSelectProps<T>) => {
  const bottomNavigatorData = useBottomNavigator();
  const isBottomNavigator = bottomNavigatorData.baseShouldDisplay;
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const { t, i18n } = useTypedTranslation(LanguageNameSpaces.COMMON);

  const menuItems = useMemo(
    () =>
      sortValues.map((value) => {
        const isSelected = sortBy === value;
        return {
          key: value,
          label: labelMapper
            ? labelMapper(value)
            : (value as unknown as string),
          onClick: () => setSortBy(value),
          style: {
            background: isSelected ? theme.colors.titleCardBgColor : undefined,
          },
        };
      }),
    [sortValues, sortBy, i18n.language]
  );

  const dropdown = (
    <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
      <DropdownButton>{t("labels.sort")}</DropdownButton>
    </Dropdown>
  );

  const select = (
    <Select
      value={sortBy}
      onChange={setSortBy}
      popupMatchSelectWidth={false}
      rounded={rounded}
      style={{ flex: 1 }}
      placeholder={placeholder ?? t("labels.sort_by")}
      options={sortValues.map((sort) => ({
        value: sort,
        label: labelMapper ? labelMapper(sort) : (sort as unknown as string),
      }))}
      showSearch={{ optionFilterProp: "label" }}
      mobileTitle={t("labels.sort_by")}
      triggerElement={
        isSingleFiltersChipsRow && isBottomNavigator ? (
          <DropdownButton onClick={() => setIsOpen(true)}>
            {t("labels.sort")}
          </DropdownButton>
        ) : undefined
      }
      open={isOpen}
      onOpenChange={setIsOpen}
    />
  );

  if (theme.isNewLook) {
    return (
      <NewLookSortControl
        sortBy={sortBy}
        setSortBy={setSortBy}
        sortDirection={sortDirection}
        setSortDirection={setSortDirection}
        sortValues={sortValues}
        placeholder={placeholder}
        labelMapper={labelMapper}
        inChipsRow={isSingleFiltersChipsRow}
      />
    );
  }

  const sortingOrderIcon =
    sortDirection === SortingOrder.ASCEND ? <Ascending /> : <Descending />;
  const onSortingOrderClick = () => setSortDirection(-1 * sortDirection);

  const SortingOrderButton = isSingleFiltersChipsRow ? (
    <RoundedButton onClick={onSortingOrderClick}>
      {sortingOrderIcon}
    </RoundedButton>
  ) : (
    <DefaultButton onClick={onSortingOrderClick}>
      {sortingOrderIcon}
    </DefaultButton>
  );

  return (
    <Flex style={{ columnGap: 8 }}>
      {isSingleFiltersChipsRow && !isBottomNavigator ? dropdown : select}
      {SortingOrderButton}
    </Flex>
  );
};

export default SortSelect;

const DefaultButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  width: 35px;
  font-size: 18px;
`;
const RoundedButton = styled(Flex)`
  background: ${(p) => p.theme.colors.filterChipsColor};
  color: ${(p) => p.theme.colors.primaryGrey};
  border-radius: 50px;
  justify-content: center;
  width: 32px;
  height: 32px;
  font-size: 16px;
  cursor: pointer;
`;
const DropdownButton = styled(Text)`
  background: ${(p) => p.theme.colors.filterChipsColor};
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  border-radius: 50px;
  padding: 5px 12px;
  height: 32px;
  cursor: pointer;
`;
